/**
 * The room (design/core-loop.md "the room, not the menu").
 *
 * One contiguous diorama: worn lino, smoke-stained tile, dead strip lights, a window with
 * moonlight coming through it, and the sign gag on the back wall — *SPIELHALLE* whose "a"
 * flickers, dies, and comes back as "ö".
 *
 * The room is lit by its machines. The only real light from outside is the window, and it
 * is cold, and it is the reason every rim in the game is the same colour.
 */

import {
  Group, Mesh, PlaneGeometry, BoxGeometry, CylinderGeometry, SphereGeometry,
  MeshBasicMaterial, RepeatWrapping, AdditiveBlending, Color, Vector3, DoubleSide,
  type Scene,
} from 'three';
import {
  NACHT, CREME, BAKELIT, SENF, BLUTROT, MATERIALS, PAPIER, INK, STAUB, mixHex,
} from '../art/palette';
import { toonSurface, toonChassis, toonMetal, toonPlastic, toonCavity, toonPlush } from '../art/toon';
import { linoleum, wallTiles, grime, halo, pool, spark, rng } from '../art/textures';
import { neonWord, neonGlyph, floorPool, type NeonPiece } from '../art/neon';
import { decalPlate } from '../art/atlas';
import { inkGroup } from '../art/outline';
import { bakeStatic } from './bake';
import { chamferBox, contactShadow } from './parts';
import { SpriteField } from '../fx/sprites';

export interface Room {
  group: Group;
  /** the sign gag, driven by the animation library */
  sign: { word: NeonPiece; a: NeonPiece; oe: NeonPiece; setGag(v: number): void };
  /** aisle x positions where cabinets stand */
  slots: Vector3[];
  dust: SpriteField;
  update(dt: number, t: number): void;
}

const ROOM_W = 150;
const ROOM_D = 62;
const ROOM_H = 40;
const BACK_Z = -26;

export function buildRoom(scene: Scene): Room {
  const g = new Group();
  g.name = 'room';

  // ---------------------------------------------------------------- floor
  const lino = linoleum();
  lino.repeat.set(14, 7);
  lino.wrapS = lino.wrapT = RepeatWrapping;
  // the floor is DARKER than the machines: the value structure of the whole game
  const floorMat = toonSurface(0x6a6258, null);
  floorMat.map = lino;
  const floor = new Mesh(new PlaneGeometry(ROOM_W, ROOM_D), floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.z = BACK_Z + ROOM_D / 2;
  floor.receiveShadow = true;
  floor.userData.noInk = true;
  g.add(floor);

  // ---------------------------------------------------------------- walls
  const tiles = wallTiles();
  tiles.repeat.set(9, 2.4);
  tiles.wrapS = tiles.wrapT = RepeatWrapping;
  const wallMat = toonSurface(0xc0ab88, grime({ seed: 21, topStain: 0.62, size: 256 }));
  wallMat.map = tiles;

  const back = new Mesh(new PlaneGeometry(ROOM_W, ROOM_H), wallMat);
  back.position.set(0, ROOM_H / 2, BACK_Z);
  back.receiveShadow = true;
  back.userData.noInk = true;
  g.add(back);

  for (const s of [-1, 1]) {
    const side = new Mesh(new PlaneGeometry(ROOM_D, ROOM_H), wallMat);
    side.position.set(s * ROOM_W / 2, ROOM_H / 2, BACK_Z + ROOM_D / 2);
    side.rotation.y = -s * Math.PI / 2;
    side.receiveShadow = true;
    side.userData.noInk = true;
    g.add(side);
  }

  // dado rail: the bakelit band at hip height that every German public room has
  const rail = new Mesh(new BoxGeometry(ROOM_W, 1.1, 0.6), toonChassis(BAKELIT.base, grime({ seed: 22 })));
  rail.position.set(0, 9.5, BACK_Z + 0.4);
  rail.userData.ink = 2.6; rail.userData.warmInk = true;
  g.add(rail);

  // ---------------------------------------------------------------- ceiling
  const ceil = new Mesh(new PlaneGeometry(ROOM_W, ROOM_D), toonSurface(0x2a2438, null));
  ceil.rotation.x = Math.PI / 2;
  ceil.position.set(0, ROOM_H, BACK_Z + ROOM_D / 2);
  ceil.userData.noInk = true;
  g.add(ceil);

  // dead strip lights — OFF. The staff went home; this is why the neon matters.
  for (const x of [-38, 0, 38]) {
    const tube = new Mesh(new CylinderGeometry(0.5, 0.5, 22, 8), toonPlastic(0x9a94a8, 0x000000, 0.2));
    tube.rotation.z = Math.PI / 2;
    tube.position.set(x, ROOM_H - 1.4, BACK_Z + 20);
    tube.userData.ink = 2.0;
    const hood = new Mesh(new BoxGeometry(24, 0.8, 2.6), toonMetal(MATERIALS.silber.shade));
    hood.position.set(x, ROOM_H - 0.5, BACK_Z + 20);
    hood.userData.ink = 2.0;
    g.add(tube, hood);
  }

  // ---------------------------------------------------------------- the window
  // cold, high on the left wall, aligned with the key light: this is the rim's sky
  const winW = 16, winH = 11;
  const winX = -ROOM_W / 2 + 0.6;
  const frame = new Mesh(chamferBox(0.8, winH + 1.6, winW + 1.6, 0.2), toonChassis(CREME.shade, grime({ seed: 23 })));
  frame.position.set(winX, 24, BACK_Z + 34);
  frame.userData.ink = 2.6;
  const glassPane = new Mesh(new PlaneGeometry(winW, winH), new MeshBasicMaterial({
    color: new Color(0x9fc4ff), transparent: true, opacity: 0.5, side: DoubleSide, toneMapped: false,
  }));
  glassPane.rotation.y = Math.PI / 2;
  glassPane.position.set(winX + 0.5, 24, BACK_Z + 34);
  glassPane.userData.noInk = true;
  // mullions
  for (const dy of [-winH / 4, winH / 4]) {
    const bar = new Mesh(new BoxGeometry(0.9, 0.42, winW), toonChassis(CREME.shade, grime({ seed: 23 })));
    bar.position.set(winX, 24 + dy, BACK_Z + 34);
    bar.userData.ink = 2.0;
    g.add(bar);
  }
  const bar2 = new Mesh(new BoxGeometry(0.9, winH, 0.42), toonChassis(CREME.shade, grime({ seed: 23 })));
  bar2.position.set(winX, 24, BACK_Z + 34);
  bar2.userData.ink = 2.0;
  g.add(frame, glassPane, bar2);

  // the light shaft: one big soft quad, angled with the key. No volumetrics, no cost.
  const shaft = new Mesh(new PlaneGeometry(46, 30), new MeshBasicMaterial({
    map: halo(2.2), color: new Color(0x9fc4ff), transparent: true, opacity: 0.035,
    blending: AdditiveBlending, depthWrite: false, side: DoubleSide, toneMapped: false,
  }));
  shaft.position.set(winX + 14, 17, BACK_Z + 40);
  shaft.rotation.set(0, Math.PI / 2, -0.5);
  shaft.userData.noInk = true;
  g.add(shaft);
  // and the puddle of moonlight it puts on the floor
  const moonPool = new Mesh(new PlaneGeometry(30, 22), new MeshBasicMaterial({
    map: pool(), color: new Color(0x9fc4ff), transparent: true, opacity: 0.16,
    blending: AdditiveBlending, depthWrite: false, toneMapped: false,
  }));
  moonPool.rotation.x = -Math.PI / 2;
  moonPool.position.set(winX + 22, 0.08, BACK_Z + 28);
  moonPool.userData.noInk = true;
  g.add(moonPool);

  // ---------------------------------------------------------------- the sign
  const SIGN_Y = 32.5, SIGN_Z = BACK_Z + 1.2;
  const word = neonWord('SPIELHALLE', { color: 'magenta', height: 5.6, hole: 6, sickness: 0.045, seed: 2 });
  word.group.position.set(0, SIGN_Y, SIGN_Z);
  const gA = neonGlyph('A', { color: 'magenta', height: 5.6, sickness: 0.9, seed: 5 });
  gA.group.position.set(word.holeX, SIGN_Y, SIGN_Z);
  const gOe = neonGlyph('Ö', { color: 'magenta', height: 5.6, sickness: 0, seed: 6 });
  gOe.group.position.set(word.holeX, SIGN_Y, SIGN_Z);
  gOe.userLocked = true;
  gOe.setIntensity(0);
  g.add(word.group, gA.group, gOe.group);

  const signPool = floorPool('magenta', 46, pool(), 0.18);
  signPool.position.set(0, 0.04, BACK_Z + 14);
  g.add(signPool);
  // the sign also washes the wall behind it
  const signWash = new Mesh(new PlaneGeometry(60, 24), new MeshBasicMaterial({
    map: halo(1.9), color: new Color(0xff8fc0), transparent: true, opacity: 0.16,
    blending: AdditiveBlending, depthWrite: false, toneMapped: false,
  }));
  signWash.position.set(0, SIGN_Y - 1, BACK_Z + 0.5);
  signWash.userData.noInk = true;
  g.add(signWash);

  // ---------------------------------------------------------------- doors & props
  const doorFrame = new Mesh(chamferBox(11, 17, 1.0, 0.2), toonChassis(BAKELIT.shade, grime({ seed: 24 })));
  doorFrame.position.set(-52, 8.5, BACK_Z + 0.6);
  doorFrame.userData.ink = 2.6; doorFrame.userData.warmInk = true;
  const doorGlass = new Mesh(new PlaneGeometry(9, 15), toonCavity(NACHT.void));
  doorGlass.position.set(-52, 8.5, BACK_Z + 1.2);
  const closed = decalPlate('GESCHLOSSEN', 6.2, {
    weight: '600', size: 52, color: PAPIER, bg: BLUTROT.shade, letterSpacing: '0.12em', pad: 18,
  });
  closed.position.set(-52, 12.0, BACK_Z + 1.32);
  closed.rotation.z = -0.05;
  g.add(doorFrame, doorGlass, closed);

  // Wechselautomat: the change machine, permanently out of coins
  const wechsel = new Mesh(chamferBox(7, 15, 4, 0.3), toonChassis(SENF.shade, grime({ seed: 25, wear: [[0.5, 0.5, 0.2]] })));
  wechsel.position.set(52, 9.1, BACK_Z + 2.4);
  wechsel.userData.ink = 2.4; wechsel.userData.warmInk = true;
  wechsel.castShadow = true;
  const wShadow = contactShadow(12, 9, 0.55);
  wShadow.position.set(52, 0.03, BACK_Z + 2.4);
  g.add(wShadow);
  const wLabel = decalPlate('GELDWECHSLER', 5.2, { weight: '600', size: 40, color: INK, bg: CREME.lit, pad: 14 });
  wLabel.position.set(52, 13.5, BACK_Z + 4.45);
  const wOut = decalPlate('LEER', 2.6, { weight: '600', size: 46, color: PAPIER, bg: BLUTROT.base, pad: 12 });
  wOut.position.set(52, 10.4, BACK_Z + 4.45);
  wOut.rotation.z = 0.08;
  g.add(wechsel, wLabel, wOut);

  // an ashtray bin and a folded chair: the specificity that sells a place
  const bin = new Mesh(new CylinderGeometry(1.5, 1.3, 5.5, 12), toonMetal(mixHex(MATERIALS.silber.shade, BAKELIT.shade, 0.5)));
  bin.position.set(-30, 2.75, BACK_Z + 5.5);
  bin.userData.ink = 2.2; bin.castShadow = true;
  const binShadow = contactShadow(6, 6, 0.5);
  binShadow.position.set(-30, 0.03, BACK_Z + 5.5);
  g.add(binShadow);
  const binTop = new Mesh(new CylinderGeometry(1.7, 1.7, 0.5, 12), toonMetal(MATERIALS.silber.base));
  binTop.position.set(-30, 5.6, BACK_Z + 5.5);
  binTop.userData.ink = 2.0;
  g.add(bin, binTop);

  // ---------------------------------------------------------------- dust
  const dust = new SpriteField(spark(), 90, AdditiveBlending);
  dust.addTo(g);
  const dr = rng(77);
  for (let i = 0; i < 60; i++) {
    dust.spawn({
      pos: [(dr() - 0.5) * 90, 2 + dr() * 26, BACK_Z + 8 + dr() * 34],
      vel: [(dr() - 0.5) * 0.35, 0.12 + dr() * 0.22, (dr() - 0.5) * 0.2],
      life: 14 + dr() * 16, size: 0.16 + dr() * 0.2,
      color: mixHex(STAUB, 0x1a1626, 0.55), fade: 'inOut',
    });
  }

  bakeStatic(g, 2.6);
  inkGroup(g, 2.6);
  scene.add(g);

  const slots = [-46, -28, -8, 12, 32, 50].map((x) => new Vector3(x, 0, BACK_Z + 12));

  let dustTimer = 0;
  let auto = true;
  const room: Room = {
    group: g,
    sign: {
      word, a: gA, oe: gOe,
      /**
       * 0 = SPIELHALLE, 1 = SPIELHÖLLE, -1 = let the tube decide.
       * Canon: the "a" is DYING, and what shows through when it dies is the "ö".
       * So the default is not a scripted gag at all — it is a broken sign, honestly
       * simulated, and the joke happens on its own schedule.
       */
      setGag(v: number) {
        auto = v < 0;
        gA.userLocked = !auto && v > 0.02;
        if (!auto) {
          if (v > 0.02) gA.setIntensity(1 - v);
          gOe.setIntensity(v);
        }
      },
    },
    slots,
    dust,
    update(dt: number, _t: number) {
      if (auto) gOe.setIntensity(Math.max(0, Math.min(1, 1 - gA.intensity * 1.7)));
      dustTimer -= dt;
      if (dustTimer <= 0) {
        dustTimer = 0.5;
        dust.spawn({
          pos: [(dr() - 0.5) * 90, 1.5, BACK_Z + 8 + dr() * 34],
          vel: [(dr() - 0.5) * 0.35, 0.15 + dr() * 0.25, (dr() - 0.5) * 0.2],
          life: 16 + dr() * 14, size: 0.16 + dr() * 0.2,
          color: mixHex(STAUB, 0x1a1626, 0.55), fade: 'inOut',
        });
      }
    },
  };
  return room;
}

// ================================================================ Der Croupier

export interface Croupier {
  group: Group;
  /** he never hurries. 0 = idle, 1 = presenting the ledger */
  setPose(v: number): void;
  update(dt: number, t: number): void;
}

/**
 * Polite, ancient, sleeve garters. Deadpan — he never lunges, never leers, and the comedy
 * is entirely in how *unbothered* he is. Silhouette: tall, narrow, slightly stooped,
 * holding a ledger he does not need to consult.
 */
export function buildCroupier(): Croupier {
  const g = new Group();
  g.name = 'croupier';

  const cloth = toonChassis(mixHex(NACHT.base, BAKELIT.shade, 0.35), grime({ seed: 41, topStain: 0.2 }));
  const shirt = toonPlastic(mixHex(CREME.lit, PAPIER, 0.5), 0x000000, 0.35);
  const skin = toonPlastic(mixHex(CREME.base, 0xd8a882, 0.55), 0x000000, 0.4);
  const metal = toonMetal(MATERIALS.gold.base);

  const legsM = new Mesh(chamferBox(3.2, 9.0, 2.4, 0.5), cloth);
  legsM.position.y = 4.6;
  legsM.userData.ink = 2.4; legsM.castShadow = true;

  const torso = new Mesh(chamferBox(4.4, 7.4, 2.8, 0.9), shirt);
  torso.position.y = 12.6;
  torso.userData.ink = 2.4; torso.castShadow = true;

  const vest = new Mesh(chamferBox(4.7, 5.6, 3.0, 0.8), cloth);
  vest.position.y = 12.0;
  vest.userData.ink = 2.4;

  const collar = new Mesh(new CylinderGeometry(1.35, 1.6, 1.1, 12), shirt);
  collar.position.y = 16.7;
  collar.userData.ink = 2.0;

  const bowtie = new Mesh(chamferBox(1.7, 0.7, 0.5, 0.2), toonPlastic(BLUTROT.shade, 0x000000, 0.2));
  bowtie.position.set(0, 16.6, 1.35);
  bowtie.userData.ink = 1.8;

  const head = new Mesh(new SphereGeometry(1.85, 16, 12), skin);
  head.position.y = 18.7;
  head.scale.set(0.92, 1.12, 0.95);
  head.userData.ink = 2.4; head.castShadow = true;

  // round spectacles: two rings and nothing behind them
  const specs = new Group();
  for (const s of [-1, 1]) {
    const ring = new Mesh(new CylinderGeometry(0.52, 0.52, 0.1, 14, 1, true), metal);
    ring.rotation.x = Math.PI / 2;
    ring.position.set(s * 0.72, 18.9, 1.62);
    ring.userData.ink = 1.4;
    const lens = new Mesh(new PlaneGeometry(0.95, 0.95), new MeshBasicMaterial({
      color: new Color(0xbfe4ff), transparent: true, opacity: 0.30, toneMapped: false, side: DoubleSide,
    }));
    lens.position.set(s * 0.72, 18.9, 1.6);
    lens.userData.noInk = true;
    specs.add(ring, lens);
  }
  const bridge = new Mesh(new BoxGeometry(0.5, 0.09, 0.09), metal);
  bridge.position.set(0, 18.9, 1.62);
  bridge.userData.ink = 1.2;
  specs.add(bridge);

  const moustache = new Mesh(chamferBox(1.5, 0.34, 0.3, 0.14), toonPlastic(0xb8b0a4, 0x000000, 0.2));
  moustache.position.set(0, 17.75, 1.55);
  moustache.userData.ink = 1.6;

  // arms, with the sleeve garters that are his whole character
  const arms = new Group();
  for (const s of [-1, 1]) {
    const upper = new Mesh(chamferBox(1.5, 5.0, 1.5, 0.6), shirt);
    upper.position.set(s * 3.0, 13.6, 0);
    upper.userData.ink = 2.2;
    const garter = new Mesh(new CylinderGeometry(0.88, 0.88, 0.5, 12), toonPlastic(MATERIALS.gold.base, 0x2a1c00, 0.2));
    garter.position.set(s * 3.0, 12.4, 0);
    garter.userData.ink = 1.6;
    const lower = new Mesh(chamferBox(1.3, 4.4, 1.3, 0.55), shirt);
    lower.position.set(s * 3.1, 9.4, 0.7);
    lower.userData.ink = 2.2;
    arms.add(upper, garter, lower);
  }

  // the ledger, held level, never opened in a hurry
  const ledger = new Group();
  const book = new Mesh(chamferBox(4.2, 0.7, 3.0, 0.14), toonChassis(BLUTROT.shade, grime({ seed: 42, topStain: 0.15 })));
  book.userData.ink = 2.2;
  const pages = new Mesh(new BoxGeometry(3.9, 0.4, 2.7), toonPlastic(PAPIER, 0x000000, 0.15));
  pages.position.y = 0.28;
  pages.userData.ink = 1.6;
  ledger.add(book, pages);
  ledger.position.set(2.2, 9.6, 2.2);
  ledger.rotation.x = -0.25;

  // breathing and pose sway live on this inner group so update() never stomps the
  // outer group's placement — fxFloorStart owns g.position/g.rotation during transit
  const body = new Group();
  body.add(legsM, torso, vest, collar, bowtie, head, specs, moustache, arms, ledger);

  const shoes = new Mesh(chamferBox(4.0, 1.1, 3.4, 0.4), toonPlastic(NACHT.deep, 0x000000, 0.2));
  shoes.position.set(0, 0.55, 0.5);
  shoes.userData.ink = 2.2;
  body.add(shoes);
  g.add(body);

  bakeStatic(g, 2.4);
  inkGroup(g, 2.4);

  let pose = 0;
  return {
    group: g,
    setPose(v: number) { pose = v; },
    update(_dt: number, t: number) {
      // he breathes, barely. Stillness is the character.
      body.position.y = Math.sin(t * 0.9) * 0.09;
      ledger.rotation.x = -0.25 - pose * 0.45;
      ledger.position.y = 9.6 + pose * 2.4;
      body.rotation.y = pose * 0.12;
    },
  };
}

/** the player: a small figure in a parka, seen from behind, permanently out of coins */
export function buildSpieler(): Group {
  const g = new Group();
  const parka = toonChassis(mixHex(SENF.shade, BAKELIT.base, 0.4), grime({ seed: 51, topStain: 0.18 }));
  const jeans = toonPlastic(mixHex(NACHT.lift, 0x3a4a78, 0.5), 0x000000, 0.4);
  const hair = toonPlush(mixHex(BAKELIT.shade, NACHT.base, 0.4));

  const body = new Mesh(chamferBox(4.0, 7.0, 2.8, 1.0), parka);
  body.position.y = 9.5; body.userData.ink = 2.4; body.castShadow = true;
  const legsM = new Mesh(chamferBox(3.2, 6.6, 2.2, 0.5), jeans);
  legsM.position.y = 3.3; legsM.userData.ink = 2.4;
  const head = new Mesh(new SphereGeometry(1.6, 14, 11), hair);
  head.position.y = 14.2; head.userData.ink = 2.4;
  const hood = new Mesh(new SphereGeometry(1.9, 14, 10, 0, Math.PI * 2, 0, Math.PI * 0.6), parka);
  hood.position.set(0, 13.4, -0.5);
  hood.rotation.x = -0.4;
  hood.userData.ink = 2.2;
  g.add(body, legsM, head, hood);
  for (const s of [-1, 1]) {
    const arm = new Mesh(chamferBox(1.3, 5.4, 1.3, 0.55), parka);
    arm.position.set(s * 2.6, 9.4, 0.2);
    arm.userData.ink = 2.2;
    g.add(arm);
  }
  bakeStatic(g, 2.4);
  inkGroup(g, 2.4);
  return g;
}
